import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Download, LogOut, Search, RefreshCw } from "lucide-react";
import { useAuth, useAuthedAxios } from "@/contexts/AuthContext";

const TABS = [
  { key: "founding-members", label: "House List", summaryKey: "founding_members", endpoint: "/admin/founding-members", columns: ["created_at", "steward_number", "name", "email", "phone", "sms_opt_in"] },
  { key: "house-alerts", label: "House Alerts", summaryKey: "house_alerts", endpoint: "/admin/house-alerts", columns: ["created_at", "name", "email", "phone", "sms_opt_in"] },
  { key: "chime-ins", label: "Chime In", summaryKey: "chime_ins", endpoint: "/admin/chime-ins", columns: ["created_at", "name", "email", "phone", "message", "sms_opt_in"] },
  { key: "event-rsvps", label: "Event RSVPs", summaryKey: "event_rsvps", endpoint: "/admin/event-rsvps", columns: ["created_at", "event_id", "name", "email", "phone", "guests", "sms_opt_in"] },
  { key: "reserve-orders", label: "Reserve Orders", summaryKey: "reserve_orders", endpoint: "/admin/reserve-orders", columns: ["created_at", "name", "email", "phone", "subtotal", "items", "sms_opt_in"] },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
function fmtCell(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.map((it) => (typeof it === "object" && it !== null ? (it.name || JSON.stringify(it)) : String(it))).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export default function Atelier() {
  const { admin, logout } = useAuth();
  const api = useAuthedAxios();
  const [active, setActive] = useState(TABS[0].key);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const currentTab = useMemo(() => TABS.find((t) => t.key === active) || TABS[0], [active]);

  // Force password change on first login — before any data loads.
  const mustChange = !!admin?.must_change_password;

  const loadSummary = useCallback(async () => {
    try { const r = await api.get("/admin/summary"); setSummary(r.data); } catch (e) { setError(e.message); }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(currentTab.endpoint);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { if (!mustChange) loadSummary(); }, [loadSummary, mustChange]);
  useEffect(() => { if (!mustChange) { loadRows(); setSearch(""); } }, [loadRows, mustChange]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, search]);

  if (mustChange) return <Navigate to="/atelier/change-password" replace />;

  const onExport = () => api.downloadCsv(`/admin/export/${currentTab.key}`, `${currentTab.key}.csv`);

  return (
    <div data-testid="atelier-dashboard" className="min-h-screen bg-obsidian text-bone">
      {/* Header */}
      <header className="border-b border-bone/10">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="label-eyebrow text-lavender">The Curator&apos;s Atelier</p>
            <p className="font-display text-bone text-xl mt-1">
              Welcome back{admin?.name ? `, ${admin.name}` : ""}.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="atelier-return-house"
              className="text-[11px] uppercase tracking-[0.3em] text-bone/60 hover:text-lavender transition-colors"
            >
              Return to the House
            </Link>
            <button
              onClick={logout}
              data-testid="atelier-logout"
              className="inline-flex items-center gap-2 px-4 py-2 border border-bone/20 hover:border-lavender hover:text-lavender text-[11px] uppercase tracking-[0.3em] transition-colors"
            >
              <LogOut size={12} /> Lock the door
            </button>
          </div>
        </div>
      </header>

      {/* Summary cards */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pt-10">
        <p className="label-eyebrow text-lavender">Tonight&apos;s Ledger</p>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="atelier-summary">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`atelier-summary-${t.key}`}
              className={`text-left border p-5 transition-colors ${
                active === t.key ? "border-lavender bg-lavender/5" : "border-bone/10 hover:border-lavender/50"
              }`}
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-bone/50">{t.label}</p>
              <p className="mt-3 font-display text-4xl text-bone tabular-nums">
                {summary ? summary[t.summaryKey] : "—"}
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* Tabs + controls */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 pt-12">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-bone/10 pb-4">
          <div className="flex flex-wrap gap-1" data-testid="atelier-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`atelier-tab-${t.key}`}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.28em] border-b-2 transition-colors ${
                  active === t.key ? "border-lavender text-lavender" : "border-transparent text-bone/60 hover:text-bone"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bone/40 pointer-events-none" />
              <input
                type="search"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="atelier-search"
                className="pl-9 pr-3 py-2 bg-transparent border border-bone/15 focus:border-lavender text-bone placeholder-bone/40 outline-none text-sm w-56 transition-colors"
              />
            </div>
            <button
              onClick={loadRows}
              disabled={loading}
              data-testid="atelier-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 border border-bone/15 hover:border-lavender hover:text-lavender text-[11px] uppercase tracking-[0.28em] transition-colors disabled:opacity-60"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onExport}
              disabled={rows.length === 0}
              data-testid="atelier-export"
              className="inline-flex items-center gap-2 px-4 py-2 bg-lavender text-obsidian text-[11px] uppercase tracking-[0.28em] hover:bg-bone transition-colors disabled:opacity-40"
            >
              <Download size={12} /> CSV
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="mx-auto max-w-[1500px] px-6 md:px-10 py-6 pb-24">
        {error && (
          <p data-testid="atelier-error" className="mt-4 text-lavender text-sm">{error}</p>
        )}

        {loading ? (
          <p data-testid="atelier-loading-rows" className="mt-16 label-eyebrow text-bone/40 animate-pulse">Fetching the ledger…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="atelier-empty" className="mt-16 border border-dashed border-bone/15 py-16 text-center">
            <p className="font-display italic text-bone/60 text-lg">The ledger is quiet.</p>
            <p className="mt-2 text-bone/40 text-sm">
              {rows.length === 0 ? "No entries yet." : "No entries match your search."}
            </p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto" data-testid="atelier-table-wrap">
            <table className="w-full text-sm" data-testid={`atelier-table-${active}`}>
              <thead>
                <tr className="border-b border-bone/10">
                  {currentTab.columns.map((c) => (
                    <th key={c} className="py-3 px-3 text-left text-[10px] uppercase tracking-[0.28em] text-bone/50 font-normal whitespace-nowrap">
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={row.id || row.created_at || i} className="border-b border-bone/5 hover:bg-lavender/[0.04] transition-colors">
                    {currentTab.columns.map((c) => {
                      // House List's Steward Number column reads from the legacy `member_number` DB field.
                      const value = c === "steward_number" ? (row.steward_number ?? row.member_number) : row[c];
                      return (
                        <td key={c} className="py-3 px-3 text-bone/80 align-top whitespace-normal max-w-xs break-words">
                          {c === "created_at" ? (
                            <span className="text-bone/50 text-xs">{fmtDate(value)}</span>
                          ) : c === "steward_number" ? (
                            <span className="font-display text-lavender">{fmtCell(value)}</span>
                          ) : (
                            fmtCell(value)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-[11px] text-bone/40 uppercase tracking-[0.28em]">
              {filtered.length} of {rows.length} entries
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
